import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { Alert, Button, Checkbox, Field, fieldErrors, Input, Select } from '../components/ui';
import { slugSchema, suggestSlug } from '@makila/shared';
import { api, post } from '../lib/api';
import { useTheme } from '../components/layout';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fetchTenantBrand, getBaseHost, type TenantBrand } from '../lib/tenant-host';
import { MakilaLogo } from '../components/brand';
import { readReferral } from './partners';

function Shell({ children, title, subtitle }: { children: ReactNode; title: string; subtitle: ReactNode }) {
  useTheme();
  return (
    <div className="grid min-h-full lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[#111111] p-12 text-white lg:flex">
        {/* Recibo do símbolo, em grande e a sangrar pela margem */}
        <svg aria-hidden className="pointer-events-none absolute -right-16 bottom-10 h-[520px] rotate-[-8deg] opacity-[.1]" viewBox="120 80 272 362" fill="none" stroke="#fff" strokeWidth="2">
          <path d="M132 120Q132 92 160 92L352 92Q380 92 380 120L380 404L359.3 430L338.7 404L318 430L297.3 404L276.7 430L256 404L235.3 430L214.7 404L194 430L173.3 404L152.7 430L132 404Z" />
          <path d="M178 300L178 160L238 236L334 132L334 300" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M178 343H334M178 371H274M292 371H334" strokeWidth="6" strokeLinecap="round" />
        </svg>
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 flex h-1.5"><span className="w-1/4 bg-[#3A3A3B]" /><span className="flex-1 bg-brand-700" /></div>
        <MakilaLogo tone="dark" className="relative h-16 w-auto self-start" />
        <div className="relative space-y-6">
          <h2 className="text-3xl font-bold leading-tight">A factura sai certa.<br /><span className="text-brand-300">À primeira.</span></h2>
          <ul className="space-y-3 text-white/80">
            {['Numeração seguida, sem buracos, e cada documento assinado com QR',
              'O ficheiro mensal para o e-Declaração gera-se num clique',
              'Recibos, notas de crédito, contas correntes e stock no mesmo sítio',
              'A factura segue por email; os pagamentos M-Pesa e e-Mola ficam registados'].map((t) => (
              <li key={t} className="flex gap-3"><CheckCircle2 className="mt-0.5 size-5 shrink-0 text-brand-400" />{t}</li>
            ))}
          </ul>
        </div>
        <p className="relative flex items-center gap-2 text-sm text-white/60"><ShieldCheck className="size-4 text-brand-400" /> Os dados de cada empresa ficam separados, cifrados em trânsito e com cópia de segurança.</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <MakilaLogo className="mb-8 h-12 w-auto lg:hidden dark:hidden" />
          <MakilaLogo tone="dark" className="mb-8 hidden h-12 w-auto dark:block lg:dark:hidden" />
          <h1 className="text-2xl font-bold">{title}</h1>
          <p className="mb-6 mt-1 text-sm text-muted">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, session, hostSlug } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [brand, setBrand] = useState<TenantBrand | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (hostSlug) fetchTenantBrand(hostSlug).then(setBrand); }, [hostSlug]);
  if (session) return <Navigate to="/" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await login(email, password, needTotp ? (useRecovery ? { recoveryCode: totp.trim() } : { totp: totp.trim() }) : undefined);
      nav((loc.state as { from?: string })?.from ?? '/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'totp_required') { setNeedTotp(true); setError(''); }
      else setError(err instanceof Error ? err.message : 'Erro');
    } finally { setBusy(false); }
  }

  return (
    <Shell title="Entrar" subtitle={<>Ainda não tem conta? <Link className="font-semibold text-brand-700" to="/registar">Experimente grátis 14 dias</Link></>}>
      {brand && (
        <div className="mb-5 rounded-xl border border-brand-100 bg-brand-50 px-4 py-3 text-sm">
          A entrar na empresa <b>{brand.tradeName || brand.legalName}</b>.
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email"><Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Password"><Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        <div className="-mt-2 text-right text-sm"><Link className="font-medium text-brand-700 hover:underline" to="/recuperar-password">Esqueci-me da password</Link></div>
        {needTotp && (
          <div className="space-y-1.5">
            {useRecovery ? (
              <Field label="Código de recuperação" hint="Um dos códigos que guardou ao activar a verificação em dois passos (cada um serve uma vez)">
                <Input key="rec" autoComplete="off" autoFocus value={totp} onChange={(e) => setTotp(e.target.value)} />
              </Field>
            ) : (
              <Field label="Código de verificação" hint="6 dígitos da aplicação autenticadora">
                <Input key="totp" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" autoFocus value={totp} onChange={(e) => setTotp(e.target.value)} />
              </Field>
            )}
            <button type="button" className="text-sm font-medium text-brand-700 hover:underline" onClick={() => { setUseRecovery(!useRecovery); setTotp(''); }}>
              {useRecovery ? 'Usar o código da aplicação' : 'Perdeu o telemóvel? Usar um código de recuperação'}
            </button>
          </div>
        )}
        {(loc.state as { reset?: boolean })?.reset && <Alert tone="success">Password alterada. Entre com a nova password.</Alert>}
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" className="w-full" size="lg" loading={busy}>Entrar</Button>
      </form>
    </Shell>
  );
}

export function SignupPage() {
  const { signup, session, hostKind } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: '', email: '', password: '', phone: '', legalName: '', slug: '', nuit: '', address: '', city: '', vatRegime: 'normal', acceptTerms: false });
  // Programa de parceiros: código do link (guardado no browser) ou escrito aqui.
  const [linkRef] = useState(() => readReferral());
  const [refCode, setRefCode] = useState(linkRef ?? '');
  const [refName, setRefName] = useState<string | null>(null);
  useEffect(() => {
    const c = refCode.trim().toUpperCase();
    if (!/^[A-Z0-9][A-Z0-9-]{2,29}$/.test(c)) { setRefName(null); return; }
    const t = setTimeout(() => { api<{ affiliate: { name: string } | null }>('GET', `/public/affiliates/code/${c}`).then((r) => setRefName(r.affiliate?.name ?? null)).catch(() => setRefName(null)); }, 400);
    return () => clearTimeout(t);
  }, [refCode]);
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugState, setSlugState] = useState<{ status: 'idle' | 'checking' | 'ok' | 'taken' | 'invalid'; msg?: string }>({ status: 'idle' });
  const [baseHost, setBaseHost] = useState('');
  // Termos e privacidade vivem no site público (domínio principal), também quando o registo é feito num subdomínio.
  const legalUrl = (path: string) => (baseHost ? `${window.location.protocol}//${baseHost}${path}` : path);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { getBaseHost().then(setBaseHost).catch(() => {}); }, []);
  // Disponibilidade do endereço, com atraso para não bater na API a cada tecla.
  useEffect(() => {
    const s = f.slug.trim();
    if (!s) { setSlugState({ status: 'idle' }); return; }
    const parsed = slugSchema.safeParse(s);
    if (!parsed.success) { setSlugState({ status: 'invalid', msg: parsed.error.issues[0]?.message }); return; }
    setSlugState({ status: 'checking' });
    const t = setTimeout(async () => {
      try {
        const r = await api<{ available: boolean }>('GET', `/public/slug-available?slug=${encodeURIComponent(parsed.data)}`);
        setSlugState({ status: r.available ? 'ok' : 'taken' });
      } catch { setSlugState({ status: 'idle' }); }
    }, 400);
    return () => clearTimeout(t);
  }, [f.slug]);
  if (session) return <Navigate to="/" replace />;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  // O endereço segue o nome enquanto não for editado à mão.
  const onLegalName = (e: { target: { value: string } }) =>
    setF((prev) => ({ ...prev, legalName: e.target.value, slug: slugEdited ? prev.slug : suggestSlug(e.target.value) }));
  const onSlug = (e: { target: { value: string } }) => { setSlugEdited(true); setF((prev) => ({ ...prev, slug: e.target.value.toLowerCase().trim() })); };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(''); setErrors({});
    try {
      const s = await signup({
        name: f.name, email: f.email, password: f.password, phone: f.phone || null, acceptTerms: f.acceptTerms,
        company: { legalName: f.legalName, slug: f.slug, nuit: f.nuit, address: f.address, city: f.city, vatRegime: f.vatRegime },
        ...(refCode.trim() ? { referral: { code: refCode.trim().toUpperCase(), source: linkRef && refCode.trim().toUpperCase() === linkRef ? 'link' : 'code' } } : {}),
      });
      // No portal/admin a app vive no subdomínio da empresa: encaminhar para lá.
      if (hostKind !== 'company') {
        const base = await getBaseHost().catch(() => window.location.host);
        window.location.href = `${window.location.protocol}//${s.tenant.slug}.${base.split(':')[0]}/`;
      } else {
        nav('/', { replace: true });
      }
    } catch (err) {
      setErrors(fieldErrors(err));
      setError(err instanceof Error ? err.message : 'Erro');
    } finally { setBusy(false); }
  }

  return (
    <Shell title="Criar conta" subtitle={<>Já tem conta? <Link className="font-semibold text-brand-700" to="/entrar">Entrar</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <fieldset className="space-y-4">
          <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">A sua empresa</legend>
          <Field label="Nome ou firma" error={errors['company.legalName']}><Input required value={f.legalName} onChange={onLegalName} placeholder="Comercial Exemplo, Lda" /></Field>
          <Field label="Endereço da empresa" error={errors['company.slug']}
            hint={slugState.status === 'ok' ? 'Disponível ✓' : slugState.status === 'taken' ? 'Já está em uso — escolha outro' : slugState.status === 'invalid' ? slugState.msg : slugState.status === 'checking' ? 'A verificar…' : 'É o endereço da sua empresa no Makila.'}>
            <div className="flex items-center gap-1.5">
              <Input required value={f.slug} onChange={onSlug} placeholder="exemplo" className="w-40" spellCheck={false} autoCapitalize="none" />
              <span className="truncate text-sm text-muted">.{baseHost || 'makila.sistemamz.com'}</span>
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="NUIT" error={errors['company.nuit']}><Input required inputMode="numeric" value={f.nuit} onChange={set('nuit')} placeholder="9 dígitos" /></Field>
            <Field label="Regime de IVA">
              <Select value={f.vatRegime} onChange={set('vatRegime')}>
                <option value="normal">Normal</option><option value="simplified">Simplificado</option><option value="exempt">Isenção</option><option value="ispc">ISPC</option>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Morada" className="col-span-2" error={errors['company.address']}><Input required value={f.address} onChange={set('address')} /></Field>
            <Field label="Cidade" error={errors['company.city']}><Input required value={f.city} onChange={set('city')} /></Field>
          </div>
        </fieldset>
        <fieldset className="space-y-4">
          <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">O seu acesso</legend>
          <Field label="Nome" error={errors.name}><Input required autoComplete="name" value={f.name} onChange={set('name')} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" error={errors.email}><Input type="email" required autoComplete="email" value={f.email} onChange={set('email')} /></Field>
            <Field label="Telefone"><Input type="tel" autoComplete="tel" value={f.phone} onChange={set('phone')} placeholder="+258 84…" /></Field>
          </div>
          <Field label="Password" error={errors.password} hint="Mínimo 10 caracteres"><Input type="password" required minLength={10} autoComplete="new-password" value={f.password} onChange={set('password')} /></Field>
          <Field label="Código de parceiro" hint={refName ? `Recomendado por ${refName} — 10% de desconto no primeiro pagamento` : 'Opcional — se um contabilista ou parceiro Makila o recomendou'}>
            <Input value={refCode} onChange={(e) => setRefCode(e.target.value.toUpperCase())} maxLength={30} className="font-mono" spellCheck={false} autoCapitalize="characters" />
          </Field>
        </fieldset>
        <Checkbox checked={f.acceptTerms} onChange={(v) => setF({ ...f, acceptTerms: v })} label={<>Li e aceito os <a href={legalUrl('/termos')} target="_blank" rel="noopener" className="font-medium text-brand-700 underline">Termos de Serviço</a> e a <a href={legalUrl('/privacidade')} target="_blank" rel="noopener" className="font-medium text-brand-700 underline">Política de Privacidade</a></>} />
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" className="w-full" size="lg" loading={busy}>Criar conta e começar</Button>
        <p className="text-center text-xs text-muted">Começa em modo formação: experimente tudo sem emitir documentos com valor fiscal.</p>
      </form>
    </Shell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try { await post('/auth/forgot', { email }); setSent(true); } catch (err) { setError(err instanceof Error ? err.message : 'Erro'); } finally { setBusy(false); }
  }
  return (
    <Shell title="Recuperar password" subtitle={<>Lembrou-se? <Link className="font-semibold text-brand-700" to="/entrar">Entrar</Link></>}>
      {sent ? <Alert tone="success" title="Verifique o seu email">Se existir uma conta com <b>{email}</b>, enviámos um endereço para definir uma nova password. É válido durante 1 hora.</Alert> : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email da conta"><Input type="email" autoComplete="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          {error && <Alert tone="error">{error}</Alert>}
          <Button type="submit" className="w-full" size="lg" loading={busy}>Enviar instruções</Button>
        </form>
      )}
    </Shell>
  );
}

/**
 * Token de uso único dos links por email. Vem no fragmento (#token=…), que o
 * browser não envia ao servidor nem deixa nos registos do proxy; os links
 * antigos (?token=…) ainda nas caixas de correio continuam a funcionar.
 */
function linkToken(sp: URLSearchParams): string {
  return new URLSearchParams(window.location.hash.slice(1)).get('token') ?? sp.get('token') ?? '';
}

export function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = linkToken(sp);
  const [f, setF] = useState({ password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault(); setError('');
    if (f.password !== f.confirm) { setError('As passwords não coincidem'); return; }
    setBusy(true);
    try { await post('/auth/reset', { token, password: f.password }); nav('/entrar', { replace: true, state: { reset: true } }); }
    catch (err) { setError(err instanceof Error ? err.message : 'Erro'); } finally { setBusy(false); }
  }
  return (
    <Shell title="Nova password" subtitle="Escolha uma password com pelo menos 10 caracteres. As sessões abertas noutros dispositivos são terminadas.">
      {!token ? <Alert tone="error">Endereço inválido. <Link className="underline" to="/recuperar-password">Peça um novo</Link>.</Alert> : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nova password" hint="Mínimo 10 caracteres"><Input type="password" autoComplete="new-password" autoFocus required minLength={10} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
          <Field label="Confirmar password"><Input type="password" autoComplete="new-password" required value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} /></Field>
          {error && <Alert tone="error">{error}</Alert>}
          <Button type="submit" className="w-full" size="lg" loading={busy}>Guardar nova password</Button>
        </form>
      )}
    </Shell>
  );
}

export function ConfirmEmailPage() {
  const [sp] = useSearchParams();
  const { session, reload } = useAuth();
  const token = linkToken(sp);
  const [state, setState] = useState<'busy' | 'ok' | 'changed' | string>(token ? 'busy' : 'Endereço inválido.');
  const started = useRef(false); // StrictMode corre o efeito duas vezes; o token só serve uma
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    post('/auth/verify-email', { token })
      .then((r) => { setState(r.result === 'changed' ? 'changed' : 'ok'); if (session) reload().catch(() => {}); })
      .catch((e) => setState(e instanceof Error ? e.message : 'Erro'));
  }, [token, session, reload]);
  return (
    <Shell title="Confirmar email" subtitle={<Link className="font-semibold text-brand-700" to={session ? '/' : '/entrar'}>{session ? 'Voltar à aplicação' : 'Entrar'}</Link>}>
      {state === 'busy' ? <Alert tone="info">A confirmar…</Alert>
        : state === 'ok' ? <Alert tone="success" title="Email confirmado">Já pode enviar documentos por email, convidar a sua equipa e activar a emissão fiscal.</Alert>
        : state === 'changed' ? <Alert tone="success" title="Email alterado">A partir de agora entra com este endereço. O endereço anterior foi avisado da mudança.</Alert>
          : <Alert tone="error">{state}</Alert>}
    </Shell>
  );
}
